/**
 * CreatableChipInput — a controlled multi-value chip input built on react-select's CreatableSelect.
 *
 * Unlike CreatableSelectField, this component:
 * - Is NOT coupled to react-hook-form — accepts plain value/onChange props (string[]),
 *   making it usable both inside a Controller render prop and with standalone local state.
 * - Supports keyboard separators (comma, space) to quickly add multiple values.
 * - Accepts an optional `validateInput` function to restrict which values can be created
 *   (e.g. digits only for eenheidnummers).
 * - Hides the dropdown indicator when no pre-defined options are provided, behaving
 *   purely as a free-text chip input.
 *
 * Use CreatableSelectField when you need built-in react-hook-form Controller, Label,
 * and FieldError wiring. Use CreatableChipInput when you need direct control over
 * value/onChange or when the field lives outside a react-hook-form context.
 */
import { useState } from 'react';
import { components as reactSelectComponents } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import CloseXs from '@/assets/icons/CloseXs';

type ChipOption = { label: string; value: string };

type CreatableChipInputProps = {
  /** Current selected values as a flat string array */
  value: string[];
  /** Called with the updated string array whenever chips are added or removed */
  onChange: (values: string[]) => void;
  /** Optional pre-defined options for the dropdown */
  options?: ChipOption[];
  /** Optional validation function — return true to accept the typed value */
  validateInput?: (input: string) => boolean;
  /** When false, disables free-text creation — only pre-defined options can be selected */
  allowCreate?: boolean;
  /** Minimum number of typed characters before the dropdown opens. Defaults to 0 (open on click). */
  minInputLength?: number;
  /** react-select inputId for label association / testing */
  inputId?: string;
  placeholder?: string;
  createLabel?: string;
  maxMenuHeight?: number;
  noOptionsMessage?: string | null;
  menuZIndex?: number;
  /** Optional leading adornment rendered inside each chip, before its label (e.g. a status icon). */
  renderValuePrefix?: (value: string) => React.ReactNode;
};

export function CustomMultiValueRemove(props: any) {
  return (
    <div style={{ display: 'flex', cursor: 'pointer' }} {...props.innerProps}>
      <CloseXs fill='currentColor' />
    </div>
  );
}

function CustomMultiValueLabelWithPrefix(renderValuePrefix: (value: string) => React.ReactNode) {
  return function CustomMultiValueLabel(props: any) {
    return (
      <reactSelectComponents.MultiValueLabel {...props}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {renderValuePrefix(props.data.value)}
          {props.children}
        </span>
      </reactSelectComponents.MultiValueLabel>
    );
  };
}

export default function CreatableChipInput({
  value,
  onChange,
  options,
  validateInput,
  allowCreate = true,
  minInputLength = 0,
  inputId,
  placeholder = 'Voeg waarde(n) toe en druk Enter',
  createLabel = 'Toevoegen:',
  maxMenuHeight = 200,
  noOptionsMessage = 'Geen opties',
  menuZIndex,
  renderValuePrefix,
}: Readonly<CreatableChipInputProps>) {
  const [inputValue, setInputValue] = useState('');
  const [menuWantsOpen, setMenuWantsOpen] = useState(false);
  const menuIsOpen = menuWantsOpen && inputValue.length >= minInputLength;
  const chipValues = value.map((v) => ({ label: v, value: v }));
  const hasOptions = options && options.length > 0;

  const addValue = (input: string) => {
    if (!allowCreate) return;
    const trimmed = input.trim();
    if (!trimmed || value.includes(trimmed)) return;
    if (validateInput && !validateInput(trimmed)) return;
    onChange([...value, trimmed]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
    }
    if (allowCreate && (e.key === ',' || e.key === ' ')) {
      e.preventDefault();
      addValue(inputValue);
      setInputValue('');
    }
  };

  return (
    <CreatableSelect
      inputId={inputId}
      isMulti
      isClearable
      isSearchable
      placeholder={placeholder}
      className='react-select-container'
      classNamePrefix='react-select'
      noOptionsMessage={() => noOptionsMessage}
      formatCreateLabel={(input) => `${createLabel} ${input}`}
      isValidNewOption={allowCreate ? undefined : () => false}
      options={options}
      maxMenuHeight={maxMenuHeight}
      menuIsOpen={menuIsOpen}
      onMenuOpen={() => setMenuWantsOpen(true)}
      onMenuClose={() => setMenuWantsOpen(false)}
      value={chipValues}
      inputValue={inputValue}
      onInputChange={(val, action) => {
        if (action.action === 'input-blur' || action.action === 'menu-close') {
          setInputValue('');
        } else {
          setInputValue(val);
        }
      }}
      onChange={(newVal) => {
        const rawValues: string[] = newVal ? newVal.map((v: any) => v.value) : [];
        const filtered = validateInput ? rawValues.filter((v) => validateInput(v)) : rawValues;
        onChange(filtered);
        setInputValue('');
      }}
      onKeyDown={handleKeyDown}
      filterOption={(option, input) => {
        if (!input || (option.data as any).__isNew__) return true;
        return option.label.toLowerCase().startsWith(input.toLowerCase());
      }}
      styles={
        menuZIndex
          ? {
              menu: (base) => ({
                ...base,
                zIndex: menuZIndex,
              }),
              control: (base) => ({
                ...base,
                borderRadius: 'var(--tractie-border-radius-m)',
              }),
            }
          : undefined
      }
      components={{
        ...(hasOptions ? {} : { DropdownIndicator: null }),
        MultiValueRemove: CustomMultiValueRemove,
        ...(renderValuePrefix ? { MultiValueLabel: CustomMultiValueLabelWithPrefix(renderValuePrefix) } : {}),
      }}
    />
  );
}
